import { c as _c } from "react/compiler-runtime";
import { Avatar, Card, Grid, HStack, Link, Text, VStack, Wrap } from '@chakra-ui/react';
import { advisingNoteUnlessRepeat } from '@src/components/sco/Advisors/cards/advisorCardText';
import { SessionReviewSummary } from '@src/components/sco/Advisors/reviews';
import { AdvisorFeaturedBadge } from '@src/components/sco/Advisors/shared/AdvisorFeaturedBadge';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ScoAdvisorProfile, ScoSessionReviewSummary, ScoUser } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
export type AdvisorDirectoryCardAdvisor = Pick<ScoAdvisorProfile, 'advisorProfileId' | 'headline'> & Partial<Pick<ScoAdvisorProfile, 'bioForAdvising' | 'advisingNote' | 'categories' | 'isFeatured'>> & {
  user?: (Pick<ScoUser, 'userId' | 'slug' | 'fullName' | 'displayName' | 'title' | 'shortBio' | 'pictureUrl'> & {
    /** Combined average + count (L41, L46). Nothing renders at 0 reviews. */
    sessionReviewSummary?: Pick<ScoSessionReviewSummary, 'averageScore' | 'count'> | null;
  }) | null;
};
type AdvisorDirectoryCardProps = {
  advisor: AdvisorDirectoryCardAdvisor;
  /** In-app profile (`/advisors/profile/{slug}`); its canonical points at `/members/{slug}` (L6). */
  profileHref: string;
  /** Under the tags (e.g. the named seat-match badge). */
  badge?: ReactNode;
  /** Primary CTA, full width at the bottom (e.g. Book or Invite). */
  actions?: ReactNode;
  /**
   * Secondary CTAs under the primary, side by side and wrapping when the card is narrow, so cards
   * stay about the same height (walk #14). The card itself and the name already open the profile.
   */
  secondaryActions?: ReactNode[];
  /** Landing block: no advising-note line, two topic tags. */
  compact?: boolean;
};

/**
 * Directory / landing advisor card. Never shows a rate — guests get `hourlyRate: null` (L1) and
 * members see pricing on the booking form. The name is a real link (crawlable, middle-click);
 * clicking anywhere else on the card opens the profile too. Shows the "Featured" label for
 * Featured/VIP (L50) and the review summary from 1 review (L41).
 */
export const AdvisorDirectoryCard = t0 => {
  const $ = _c(92);
  const {
    advisor,
    profileHref,
    badge,
    actions,
    secondaryActions,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const router = useRouter();
  const user = advisor.user;
  const name = user?.fullName || user?.displayName || "Advisor";
  let T0;
  let T1;
  let secondary;
  let t10;
  let t11;
  let t12;
  let t13;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== advisor.advisingNote || $[1] !== advisor.bioForAdvising || $[2] !== advisor.categories || $[3] !== advisor.headline || $[4] !== advisor.isFeatured || $[5] !== badge || $[6] !== compact || $[7] !== name || $[8] !== profileHref || $[9] !== router || $[10] !== secondaryActions || $[11] !== user) {
    const categories = (advisor.categories ?? []).filter(_temp);
    const headline = user?.shortBio || advisor.headline || user?.title;
    let t14;
    if ($[27] !== advisor.advisingNote || $[28] !== advisor.bioForAdvising || $[29] !== compact || $[30] !== headline) {
      t14 = compact ? null : advisingNoteUnlessRepeat(advisor.bioForAdvising || advisor.advisingNote, headline);
      $[27] = advisor.advisingNote;
      $[28] = advisor.bioForAdvising;
      $[29] = compact;
      $[30] = headline;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    const note = t14;
    let t15;
    if ($[32] !== secondaryActions) {
      t15 = secondaryActions ?? [];
      $[32] = secondaryActions;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== t15) {
      t16 = t15.filter(Boolean);
      $[34] = t15;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    secondary = t16;
    T1 = Card.Root;
    t9 = "pointer";
    t10 = "full";
    t11 = "hidden";
    t12 = "interactive";
    if ($[36] !== profileHref || $[37] !== router) {
      t13 = () => router.push(profileHref);
      $[36] = profileHref;
      $[37] = router;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    T0 = Card.Body;
    t2 = "center";
    t3 = compact ? 3 : 4;
    t4 = "center";
    const t17 = compact ? "lg" : "xl";
    let t18;
    if ($[39] !== user) {
      t18 = user?.pictureUrl ? <Avatar.Image alt="" src={user.pictureUrl} /> : null;
      $[39] = user;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    let t19;
    if ($[41] !== name) {
      t19 = <Avatar.Fallback name={name} />;
      $[41] = name;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    if ($[43] !== t17 || $[44] !== t18 || $[45] !== t19) {
      t5 = <Avatar.Root size={t17}>{t18}{t19}</Avatar.Root>;
      $[43] = t17;
      $[44] = t18;
      $[45] = t19;
      $[46] = t5;
    } else {
      t5 = $[46];
    }
    const t20 = compact ? "md" : "lg";
    let t21;
    if ($[47] !== name || $[48] !== profileHref) {
      t21 = <NextLink href={profileHref}>{name}</NextLink>;
      $[47] = name;
      $[48] = profileHref;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    let t22;
    if ($[50] !== t20 || $[51] !== t21) {
      t22 = <Link asChild={true} color="inherit" fontSize={t20} fontWeight="semibold" textDecoration="none" onClick={_temp2}>{t21}</Link>;
      $[50] = t20;
      $[51] = t21;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    let t23;
    if ($[53] !== advisor.isFeatured) {
      t23 = <AdvisorFeaturedBadge isFeatured={advisor.isFeatured} size="sm" />;
      $[53] = advisor.isFeatured;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] !== t22 || $[56] !== t23) {
      t24 = <HStack columnGap={2} flexWrap="wrap" justify="center" minWidth={0} rowGap={1}>{t22}{t23}</HStack>;
      $[55] = t22;
      $[56] = t23;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    const t25 = user?.sessionReviewSummary?.averageScore;
    const t26 = user?.sessionReviewSummary?.count;
    let t27;
    if ($[58] !== t25 || $[59] !== t26) {
      t27 = <SessionReviewSummary averageScore={t25} count={t26} size="sm" />;
      $[58] = t25;
      $[59] = t26;
      $[60] = t27;
    } else {
      t27 = $[60];
    }
    const t28 = compact ? 2 : 3;
    let t29;
    if ($[61] !== headline || $[62] !== t28) {
      t29 = <Text color="colorPalette.fg/80" fontSize="sm" fontWeight="medium" lineClamp={t28}>{headline}</Text>;
      $[61] = headline;
      $[62] = t28;
      $[63] = t29;
    } else {
      t29 = $[63];
    }
    let t30;
    if ($[64] !== note) {
      t30 = note && <Text color="colorPalette.fg/55" fontSize="xs" lineClamp={2}>{note}</Text>;
      $[64] = note;
      $[65] = t30;
    } else {
      t30 = $[65];
    }
    if ($[66] !== t24 || $[67] !== t27 || $[68] !== t29 || $[69] !== t30) {
      t6 = <VStack gap={1} minWidth={0} width="full">{t24}{t27}{t29}{t30}</VStack>;
      $[66] = t24;
      $[67] = t27;
      $[68] = t29;
      $[69] = t30;
      $[70] = t6;
    } else {
      t6 = $[70];
    }
    t7 = badge;
    t8 = categories.length > 0 && <Wrap gap={1} justify="center">{categories.slice(0, compact ? 2 : 4).map(_temp3)}</Wrap>;
    $[0] = advisor.advisingNote;
    $[1] = advisor.bioForAdvising;
    $[2] = advisor.categories;
    $[3] = advisor.headline;
    $[4] = advisor.isFeatured;
    $[5] = badge;
    $[6] = compact;
    $[7] = name;
    $[8] = profileHref;
    $[9] = router;
    $[10] = secondaryActions;
    $[11] = user;
    $[12] = T0;
    $[13] = T1;
    $[14] = secondary;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    T0 = $[12];
    T1 = $[13];
    secondary = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t14;
  if ($[71] !== actions || $[72] !== secondary) {
    t14 = (actions || secondary.length > 0) && <VStack gap={2} marginTop="auto" width="full">{actions}{secondary.length > 0 && <Grid gap={2} templateColumns="repeat(auto-fit, minmax(8.5rem, 1fr))" width="full">{secondary}</Grid>}</VStack>;
    $[71] = actions;
    $[72] = secondary;
    $[73] = t14;
  } else {
    t14 = $[73];
  }
  let t15;
  if ($[74] !== T0 || $[75] !== t14 || $[76] !== t2 || $[77] !== t3 || $[78] !== t4 || $[79] !== t5 || $[80] !== t6 || $[81] !== t7 || $[82] !== t8) {
    t15 = <T0 alignItems={t2} gap={t3} textAlign={t4}>{t5}{t6}{t7}{t8}{t14}</T0>;
    $[74] = T0;
    $[75] = t14;
    $[76] = t2;
    $[77] = t3;
    $[78] = t4;
    $[79] = t5;
    $[80] = t6;
    $[81] = t7;
    $[82] = t8;
    $[83] = t15;
  } else {
    t15 = $[83];
  }
  let t16;
  if ($[84] !== T1 || $[85] !== t10 || $[86] !== t11 || $[87] !== t12 || $[88] !== t13 || $[89] !== t15 || $[90] !== t9) {
    t16 = <T1 cursor={t9} height={t10} overflow={t11} variant={t12} onClick={t13}>{t15}</T1>;
    $[84] = T1;
    $[85] = t10;
    $[86] = t11;
    $[87] = t12;
    $[88] = t13;
    $[89] = t15;
    $[90] = t9;
    $[91] = t16;
  } else {
    t16 = $[91];
  }
  return t16;
};
function _temp(category) {
  return Boolean(category?.title);
}
function _temp2(event) {
  return event.stopPropagation();
}
function _temp3(category_0) {
  return <SpecialtyTag key={category_0.key ?? category_0.title} label={category_0.title} />;
}